import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Zap, LogOut, Plus, ShieldCheck, Megaphone, HelpCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { ThemeToggle } from "./ThemeToggle";
import { Button } from "./ui/button";

const planStyle = {
  pro: "bg-primary/10 text-primary",
  family: "bg-[#3A6EA5]/10 text-[#3A6EA5]",
};

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const plan = user?.plan || "free";
  const [hasNews, setHasNews] = useState(false);

  useEffect(() => {
    api.get("/updates").then((r) => {
      const latest = r.data.latest_date;
      if (latest && latest > (localStorage.getItem("vr_updates_seen") || "")) setHasNews(true);
    }).catch(() => {});
  }, []);

  return (
    <header className="sticky top-0 z-40 glass-panel border-b border-border/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/dashboard" data-testid="nav-logo" className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
            <Zap className="w-5 h-5 text-primary-foreground" />
          </span>
          <span className="font-heading font-800 font-extrabold text-lg tracking-tight">VoltRoute</span>
          {plan !== "free" && (
            <span data-testid="nav-plan-badge" className={`text-[10px] font-extrabold uppercase tracking-wider rounded-full px-2.5 py-1 ${planStyle[plan]}`}>{plan}</span>
          )}
        </Link>
        <div className="flex items-center gap-1.5 sm:gap-3">
          <Button
            data-testid="nav-new-trip-button"
            onClick={() => navigate("/nieuwe-trip")}
            className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground active:scale-95 transition-[transform,background-color] duration-200"
          >
            <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Nieuwe trip</span>
          </Button>
          <button data-testid="nav-updates-link" onClick={() => { setHasNews(false); navigate("/wat-is-nieuw"); }} title="Wat is nieuw & roadmap"
            className="relative p-2 rounded-full hover:bg-secondary text-primary transition-[background-color] duration-200">
            <Megaphone className="w-4 h-4" />
            {hasNews && (
              <span data-testid="nav-updates-badge" className="absolute -top-1.5 -right-2.5 rounded-full bg-red-500 text-white text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 leading-none shadow-sm animate-pulse">
                Nieuw
              </span>
            )}
          </button>
          <button data-testid="nav-help-link" onClick={() => navigate("/hoe-werkt-het")} title="Hoe werkt VoltRoute?"
            className="p-2 rounded-full hover:bg-secondary text-primary transition-[background-color] duration-200">
            <HelpCircle className="w-4 h-4" />
          </button>
          {user?.role === "admin" && (
            <button data-testid="nav-admin-link" onClick={() => navigate("/admin")} title="Admin"
              className="p-2 rounded-full hover:bg-secondary text-primary transition-[background-color] duration-200">
              <ShieldCheck className="w-4 h-4" />
            </button>
          )}
          <ThemeToggle />
          <span className="hidden sm:block text-sm text-muted-foreground">{user?.name}</span>
          <button
            data-testid="nav-logout-button"
            onClick={async () => { await logout(); navigate("/"); }}
            className="p-2 rounded-full hover:bg-secondary transition-[background-color] duration-200"
            title="Uitloggen"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
};
